/**
 * 🚫 GLOBAL RULE:
 * NO HARDCODED LOCATIONS.
 * Location must ALWAYS come from user input.
 * Violations will break spatial integrity.
 */

import { getAuth } from 'firebase/auth';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { firebaseApp } from '../auth/firebaseConfig';
import { contextState, formatQuotaGuidance } from './contextState';
import { environmentController } from '../environment/environmentController';
import { GatewayHealthService } from '../services/gateway/GatewayHealthService';
import { searchVenues } from '../services/spatialSearchService';
import { spatialTool } from '../tools/SpatialTool';
import { traceRecorder } from '../spatial/trace/traceRecorder';
import { traceStore } from '../spatial/trace/traceStore';
import { copilotEngine } from '../spatial/copilot/copilotEngine';
import { ForensicLogger } from '../observability/ForensicLogger';
import { coerceCoordinates } from '../spatial/coordinateValidator';
import { LocationResolver } from '../services/locationResolver';
import { IntentEngine } from '../services/intentEngine';
import { useWingmanStore } from '../store/useWingmanStore';
import { replaySystem } from '../spatial/replaySystem';
import { spatialIntelligenceService } from '../services/spatialIntelligenceService';
import { geoReasoningEngine } from '../services/geoReasoningEngine';
import { WingmanAnalytics } from '../services/analyticsService';
import { spatialEventBus } from '../utils/spatialBridge';
import { telemetry } from '../spatial/utils/telemetry';
import { voiceState } from '../state/voiceState';
import { indoorMapController, INDOOR_AIRPORTS, AIRPORT_ALIASES } from '../spatial/indoorMapController';
import { ResearchIntentResolver } from '../services/research/ResearchIntentResolver';
import { ResearchActivityController } from '../services/research/ResearchActivityController';
import { ReportIntentClassifier } from '../services/intelligence/ReportIntentClassifier';
import { reportVoiceLifecycle } from '../services/voice/ReportVoiceLifecycle';
import { reportConfirmationCoordinator } from '../services/report/ReportConfirmationCoordinator';
import { generateMockCanonicalReport } from '../services/report/MockReportGenerator';
import { workspaceRegistry } from '../workspaces/WorkspaceRegistry';
import { workspaceAuthority } from '../workspaces/workspaceAuthority';

const reportSessionCoordinator = new ReportIntentClassifier().createSessionCoordinator();

export interface RealEstateRequestPropertyContext {
    id: string;
    address?: string;
    listingPrice?: number;
    taxAssessment?: number;
    providerEstimate?: number;
    providerRentEstimate?: number;
    beds?: number;
    baths?: number;
    squareFeet?: number;
    propertyType?: string;
    listingStatus?: string;
}

export interface RealEstateRequestContext {
    activeWorkspace?: 'real-estate-intelligence';
    realEstate?: {
        mode: 'buy' | 'rent' | 'sold';
        selectedProperty: RealEstateRequestPropertyContext | null;
        savedCount: number;
        compareCount: number;
    };
}

/**
 * Bounded Real Estate -> Main Chat request pack.
 * Lifecycle authority:
 * - When real-estate-intelligence is active: returns active workspace state and current selection (or null).
 * - When outside real-estate-intelligence (e.g. Main Wingman): retains and forwards valid selected property
 *   context if present in contextState.
 * - When selected property is null, explicitly cleared, or contextState is reset: excludes property context
 *   to prevent stale leaks.
 */
export function buildRealEstateRequestContext(): RealEstateRequestContext {
    const isRealtyActive = workspaceRegistry.getActiveWorkspaceId() === 'real-estate-intelligence';
    const rec = contextState.getData()?.realEstateContext;

    if (!rec) {
        return isRealtyActive ? { activeWorkspace: 'real-estate-intelligence' } : {};
    }

    const p = rec.selectedProperty;
    const hasValidSelection = Boolean(p && p.id);

    if (!isRealtyActive && !hasValidSelection) {
        return {};
    }

    return {
        ...(isRealtyActive ? { activeWorkspace: 'real-estate-intelligence' as const } : {}),
        realEstate: {
            mode: rec.mode,
            selectedProperty: p ? {
                id: p.id,
                address: p.formattedAddress || [p.streetAddress, p.city, p.state, p.postalCode].filter(Boolean).join(', ') || [p.city, p.state].filter(Boolean).join(', ') || undefined,
                listingPrice: p.listingPrice,
                taxAssessment: p.taxAssessment,
                providerEstimate: p.providerEstimate,
                providerRentEstimate: p.providerRentEstimate,
                beds: p.beds,
                baths: p.baths,
                squareFeet: p.squareFeet,
                propertyType: p.propertyType,
                listingStatus: p.listingStatus,
            } : null,
            savedCount: rec.savedCount,
            compareCount: rec.compareCount,
        },
    };
}

export interface FlightRequestItemContext {
    id: string;
    airline?: string;
    flightNumber?: string;
    origin?: string;
    destination?: string;
    departureDate?: string;
    returnDate?: string;
    departureTime?: string;
    arrivalTime?: string;
    price?: number;
    currency?: string;
    durationMinutes?: number;
    stopsCount?: number;
    cabinClass?: string;
}

export interface TripadvisorRequestContext {
    placeId: string;
    placeName: string;
    averageRating: number;
    totalReviews: number;
    categoryRatings?: Record<string, number>;
    topReviews?: Array<{
        title: string;
        rating: number;
        snippet: string;
        type?: string;
        responseSnippet?: string;
    }>;
}

export interface FlightRequestContext {
    activeWorkspace?: 'flight-showroom';
    flights?: {
        origin?: string;
        destination?: string;
        selectedDeal?: {
            id: string;
            name: string;
            country?: string;
            airportCode?: string;
            price?: number;
            currency?: string;
            savingsPercent?: number;
            description?: string;
        } | null;
        selectedFlight?: FlightRequestItemContext | null;
        tripadvisor?: TripadvisorRequestContext | null;
    };
}

/**
 * Bounded Flight Showroom -> Main Chat request pack.
 * Lifecycle authority:
 * - When flight-showroom is active: returns active workspace state and current deal/flight context.
 * - When outside flight-showroom (e.g. Main Wingman): retains and forwards valid selected deal/flight
 *   context if present in contextState.
 * - When deal and flight are null, explicitly cleared, or contextState is reset: excludes flight context
 *   to prevent stale leaks.
 */
export function buildFlightRequestContext(): FlightRequestContext {
    const isFlightActive = workspaceRegistry.getActiveWorkspaceId() === 'flight-showroom';
    const data = contextState.getData();

    if (!data) {
        return isFlightActive ? { activeWorkspace: 'flight-showroom' } : {};
    }

    const deal = data.selectedDeal;
    const obs = data.liveObservation;
    const selectedFlightId = data.selectedFlightId;

    let selectedFlight: FlightRequestItemContext | null = null;
    if (obs) {
        selectedFlight = {
            id: obs.id || 'live-obs',
            airline: obs.airline,
            flightNumber: obs.flightNumber,
            origin: obs.origin,
            destination: obs.destination,
            departureDate: obs.departureDate,
            returnDate: obs.returnDate,
            departureTime: obs.departureTime,
            arrivalTime: obs.arrivalTime,
            price: obs.price?.amount,
            currency: obs.price?.currency || 'USD',
            durationMinutes: obs.durationMinutes,
            stopsCount: obs.stopsCount,
            cabinClass: obs.cabinClass,
        };
    } else if (deal?.flights && Array.isArray(deal.flights) && deal.flights.length > 0) {
        const matching = deal.flights.find((f: any) => f.id === selectedFlightId) || deal.flights[0];
        if (matching) {
            selectedFlight = {
                id: matching.id || 'deal-flight',
                airline: matching.airline,
                flightNumber: matching.flightNumber && matching.flightNumber !== 'NOT_RETURNED' ? matching.flightNumber : undefined,
                origin: matching.originAirport || data.origin?.code || 'CDG',
                destination: matching.destAirport || deal.airportCode,
                departureTime: matching.departureTime && matching.departureTime !== 'NOT_RETURNED' ? matching.departureTime : undefined,
                arrivalTime: matching.arrivalTime && matching.arrivalTime !== 'NOT_RETURNED' ? matching.arrivalTime : undefined,
                price: matching.price,
                currency: matching.currency || deal.currency || 'USD',
                stopsCount: typeof matching.stops === 'number' ? matching.stops : (matching.stops === 'Nonstop' ? 0 : 1),
            };
        }
    }

    const taContext = contextState.getTripadvisorContext?.();
    const tripadvisor: TripadvisorRequestContext | null = taContext && taContext.placeName ? {
        placeId: taContext.placeId,
        placeName: taContext.placeName,
        averageRating: taContext.averageRating,
        totalReviews: taContext.totalReviews,
        categoryRatings: taContext.categoryRatings,
        topReviews: taContext.topReviews,
    } : null;

    const hasValidSelection = Boolean((deal && deal.id) || (selectedFlight && selectedFlight.id) || (tripadvisor && tripadvisor.placeId));

    if (!isFlightActive && !hasValidSelection) {
        return {};
    }

    return {
        ...(isFlightActive ? { activeWorkspace: 'flight-showroom' as const } : {}),
        flights: {
            origin: data.origin?.city ? `${data.origin.city} (${data.origin.code || data.origin.airportCode || 'CDG'})` : undefined,
            destination: deal?.name ? `${deal.name} (${deal.airportCode})` : (selectedFlight?.destination || undefined),
            selectedDeal: deal ? {
                id: deal.id,
                name: deal.name,
                country: deal.country,
                airportCode: deal.airportCode,
                price: deal.price,
                currency: deal.currency,
                savingsPercent: deal.savingsPercent,
                description: deal.description,
            } : null,
            selectedFlight,
            ...(tripadvisor ? { tripadvisor } : {}),
        },
    };
}

export interface RadioRequestContext {
    activeWorkspace?: 'radio';
    radio?: {
        displayState: 'selected' | 'playing' | 'paused' | 'stopped' | 'failed' | 'loading' | 'idle';
        playbackState: string;
        activeStation: {
            id: string;
            name: string;
            country?: string;
            codec?: string;
            bitrate?: number;
            tags?: string;
            coordinates?: [number, number];
            provenance?: string;
            homepage?: string;
        } | null;
        visibleStationCount: number;
        focusedMarket?: string;
    };
}

/**
 * Bounded Radio Workspace -> AI Request Context Pack.
 * Lifecycle authority:
 * - When radio is active: returns active workspace state and current station/playback context.
 * - When outside radio workspace: forwards active station context if primed.
 * - Excludes synthetic/decorative FM frequencies, providing real directory station metadata.
 */
export function buildRadioRequestContext(): RadioRequestContext {
    const isRadioActive = workspaceRegistry.getActiveWorkspaceId() === 'radio';
    const ctx = (typeof window !== 'undefined' && (window as any).__WINGMAN_RADIO_CONTEXT__) || null;

    if (!ctx) {
        return isRadioActive ? { activeWorkspace: 'radio' } : {};
    }

    if (!isRadioActive && !ctx.activeStation) {
        return {};
    }

    const st = ctx.activeStation;
    return {
        ...(isRadioActive ? { activeWorkspace: 'radio' as const } : {}),
        radio: {
            displayState: ctx.displayState || (ctx.playbackState === 'idle' && st ? 'selected' : ctx.playbackState),
            playbackState: ctx.playbackState,
            activeStation: st ? {
                id: st.stationuuid,
                name: st.name,
                country: st.country,
                codec: st.codec,
                bitrate: st.bitrate,
                tags: st.tags,
                coordinates: st.coordinates,
                provenance: st.provenance,
                homepage: st.homepage
            } : null,
            visibleStationCount: ctx.visibleStationCount || 0,
            focusedMarket: ctx.focusedMarket
        }
    };
}

// Phase 6.5: Cache browser geolocation
let cachedLocation: { lat: number; lon: number } | null = null;

/** @internal - For testing only */
export function __resetCachedLocation() {
    cachedLocation = null;
}

// ── DESTINATION CACHE ─────────────────────────────────────────────────────────
// Eliminates redundant gateway queries for common destinations.
// Cache entries expire after TTL (default: 5 minutes).
// Only caches deterministic data (geocoding results, not live conversations).
interface CacheEntry {
    result: any;
    timestamp: number;
    ttl: number;
}

const destinationCache = new Map<string, CacheEntry>();
const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCachedDestination(query: string): any | null {
    const normalized = query.toLowerCase().trim();
    const entry = destinationCache.get(normalized);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > entry.ttl) {
        destinationCache.delete(normalized);
        return null;
    }
    return entry.result;
}

function setCachedDestination(query: string, result: any, ttl: number = DEFAULT_CACHE_TTL): void {
    const normalized = query.toLowerCase().trim();
    destinationCache.set(normalized, { result, timestamp: Date.now(), ttl });
}

export function clearDestinationCache(): void {
    destinationCache.clear();
}

// 🕒 EVENT QUEUE SYSTEM: Prevents race conditions during bootstrap
const eventQueue: any[] = [];

function getSafeEventBus() {
    if (spatialEventBus) {
        return spatialEventBus;
    }

    if (typeof window !== 'undefined' && (window as any).WingmanEventBus) {
        return (window as any).WingmanEventBus;
    }

    telemetry.warn('CONTEXT_ENGINE', 'EVENT_BUS_NOT_READY', { action: 'DELAYING_DISPATCH' });
    return null;
}

export function queueEvent(event: any) {
    telemetry.info('CONTEXT_ENGINE', 'EVENT_QUEUED', { action: event.action });
    eventQueue.push(event);
}

export function flushEventQueue() {
    const bus = getSafeEventBus();
    if (!bus) return;

    if (eventQueue.length > 0) {
        telemetry.info('CONTEXT_ENGINE', 'FLUSHING_QUEUE', { count: eventQueue.length });
        while (eventQueue.length > 0) {
            const event = eventQueue.shift();
            // 🛡️ ATOM S9.0.6: Standardize to intent flow
            bus.emit('SPATIAL_INTENT', {
                intentType: event.action === 'CAMERA_FIT_BOUNDS' ? 'FIT_BOUNDS' : 'NAVIGATE',
                source: event.source || 'AI',
                payload: event.params,
                timestamp: Date.now()
            });
        }
    }
}

// ────────────────────────────────────────────────────────────
// 🧩 ATOM V5.2.1 — Query Normalization
// Consistent lowercasing + trimming for all intent detection.
// No side effects. Null-safe.
// ────────────────────────────────────────────────────────────
export function normalizeQuery(query: string): string {
    if (!query) return '';
    return query.toLowerCase().trim();
}

// ────────────────────────────────────────────────────────────
// 🧩 ATOM V5.2.2 — Vault Phrase Detection
// Deterministic phrase matching against known vault intents.
// Input MUST already be normalized. No regex. No side effects.
// ────────────────────────────────────────────────────────────
const VAULT_PHRASES = [
    'what have i saved',
    'show my saved',
    'my saved places',
    'saved videos',
    'what did i save',
    'things i saved',
];

export function matchesVaultPhrase(query: string): boolean {
    if (!query) return false;
    return VAULT_PHRASES.some(phrase => query.includes(phrase));
}

// 🌍 GLOBAL FAILSAFE: Neutral starting point for global navigation
const WORLD_VIEW = { lat: 20, lon: 0, zoom: 1.5 };



function isValidCoordinate(lat: any, lon: any): boolean {
    if (lat === null || lon === null || lat === undefined || lon === undefined) return false;
    if (isNaN(lat) || isNaN(lon)) return false;

    // 🛡️ GPS Range Validation
    if (lat < -90 || lat > 90) return false;
    if (lon < -180 || lon > 180) return false;

    // Specific business rule: [0,0] is often a sign of failed geocoding or default null
    if (lat === 0 && lon === 0) return false;

    return true;
}

function sanitizeInput(input: string): string {
    if (!input) return "";
    // 🛡️ SECURITY: Strip HTML tags to prevent XSS/Injection
    return input.replace(/<[^>]*>?/gm, '').trim();
}

function getBrowserLocation(): Promise<{ lat: number; lon: number } | null> {
    return new Promise((resolve) => {
        if (cachedLocation) return resolve(cachedLocation);

        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            telemetry.warn('CONTEXT_ENGINE', 'GEOLOCATION_UNAVAILABLE');
            return resolve(null);
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                cachedLocation = {
                    lat: pos.coords.latitude,
                    lon: pos.coords.longitude
                };
                telemetry.log({ subsystem: 'CONTEXT_ENGINE', event: 'GPS_RESOLVED', level: 'INFO', payload: { source: 'REAL_GPS', location: cachedLocation } });
                resolve(cachedLocation);
            },
            async (err) => {
                telemetry.warn('CONTEXT_ENGINE', 'GPS_FAILED', { code: err.code, message: err.message });

                // 🌍 ATOM S3: IP-Based Geolocation Fallback
                telemetry.info('CONTEXT_ENGINE', 'IP_GEOLOCATION_FALLBACK');
                try {
                    const resolver = new LocationResolver();
                    const ipResult = await resolver.resolveViaIP();
                    if (ipResult) {
                        cachedLocation = {
                            lat: ipResult.coords[1],
                            lon: ipResult.coords[0]
                        };
                        telemetry.log({ subsystem: 'CONTEXT_ENGINE', event: 'GPS_RESOLVED', level: 'INFO', payload: { source: 'IP_GEOLOCATION', location: cachedLocation } });
                        return resolve(cachedLocation);
                    }
                } catch (ipErr) {
                    telemetry.error('CONTEXT_ENGINE', 'IP_LOOKUP_FAILED', { error: ipErr });
                }

                resolve(null);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0 // 🥇 TIER 2: Force fresh data
            }
        );
    });
}

/**
 * 🛡️ INTENT-AWARE LOCATION GUARD
 * 
 * MODIFIED: Removed hardcoded Miami/Tokyo pilot drift guards to allow 
 * global compatibility with local backend services.
 */
function assertNoDefaultLocation(
    center: { lat: number; lon: number } | null,
    userInput?: string
) {
    if (!center) return;

    // Skip WORLD_VIEW fallback (neutral starting point)
    if (center.lat === 20 && center.lon === 0) return;

    // 🔍 Forensic Logging of location decisions
    telemetry.log({
        subsystem: 'CONTEXT_ENGINE',
        event: 'LOCATION_VALIDATED',
        level: 'INFO',
        payload: { input: userInput, coords: center }
    });
}

let _watchId: number | null = null;

export const contextEngine = {
    activeVoiceSessionId: null as string | null,

    setActiveVoiceSessionId(sessionId: string | null): void {
        this.activeVoiceSessionId = sessionId;
        (this as any).currentSessionId = sessionId;
    },

    clearVoiceSessionId(closingSessionId?: string): void {
        if (!closingSessionId || this.activeVoiceSessionId === closingSessionId) {
            this.activeVoiceSessionId = null;
            (this as any).currentSessionId = null;
        }
    },

    getActiveVoiceSessionId(): string | null {
        return this.activeVoiceSessionId;
    },
    /**
     * 🛰️ ATOM S3: Unified Tracking Authority
     */
    startTracking() {
        if (_watchId !== null) return;
        if (typeof navigator === 'undefined' || !navigator.geolocation) return;

        _watchId = navigator.geolocation.watchPosition(
            (pos) => {
                // 🔒 LOCATION LOCK GUARD: Do not overwrite Context State if we are spatially locked
                if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.getLocationLock) {
                    if ((window as any).WingmanSpatialControl.getLocationLock()) {
                        telemetry.throttledLog('TRACKING_GPS_UPDATE', { subsystem: 'CONTEXT_ENGINE', event: 'GPS_IGNORED_LOCKED', level: 'DEBUG' }, 10000);
                        return;
                    }
                }

                const center = { lat: pos.coords.latitude, lon: pos.coords.longitude };
                telemetry.throttledLog('TRACKING_GPS_UPDATE', { subsystem: 'CONTEXT_ENGINE', event: 'GPS_UPDATE', level: 'INFO', payload: { center } }, 10000);

                // Update central context state
                contextState.setState('tracking', {
                    center,
                    source: 'REAL_TIME_GPS'
                }, 'map');
            },
            (err) => telemetry.warn('CONTEXT_ENGINE', 'TRACKING_GPS_ERROR', { err: err.message }),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
        );
    },

    stopTracking() {
        if (_watchId !== null && typeof navigator !== 'undefined') {
            navigator.geolocation.clearWatch(_watchId);
            _watchId = null;
            telemetry.info('CONTEXT_ENGINE', 'TRACKING_STOPPED');
        }
    },
    async init() {
        telemetry.info('CONTEXT_ENGINE', 'INITIALIZING');

        // 🥇 FIX 1: FORCE ORB-FIRST STATE ON INIT
        // Explicitly set mapMode to false so the Orb scene is the default
        contextState.setState('idle', {
            mapMode: false,
            center: { lat: WORLD_VIEW.lat, lon: WORLD_VIEW.lon },
            zoom: WORLD_VIEW.zoom
        });
        contextState.setSystemMode('idle');

        // Initial health check
        GatewayHealthService.on("gateway_online", (state) => {
            if (contextState.getConnectionStatus() !== 'active') {
                contextState.setConnectionStatus('online');
            }
        });

        GatewayHealthService.on("gateway_offline", (state) => {
            if (contextState.getConnectionStatus() !== 'active') {
                contextState.setConnectionStatus('offline');
            }
        });

        // GatewayHealthService.start(); // Managed by BootManager

        // PHASE 2: FORCE GLOBAL DEFAULT STATE
        contextState.setData({
            ...contextState.getData(),
            center: { lat: WORLD_VIEW.lat, lon: WORLD_VIEW.lon },
            zoom: WORLD_VIEW.zoom,
            mapMode: false,
            panelMode: 'none'
        });

        // Initial location resolve (Background cache only, NO auto-mount)
        getBrowserLocation().catch(e => telemetry.warn('CONTEXT_ENGINE', 'INIT_GPS_FAILED', { e }));

        telemetry.info('CONTEXT_ENGINE', 'INITIALIZED', { state: 'NATURAL_STATE_ORB' });

        // PHASE 1: NO automatic map activation on timers

        // 🧠 ACTIVATE STRATEGIC REASONING ENGINE
        geoReasoningEngine.start();
    },

    openVault() {
        telemetry.info('CONTEXT_ENGINE', 'VAULT_ACCESS_TRIGGERED');
        contextState.setSystemMode('vault');
        contextState.setPanelMode('vault');
        contextState.setData({ ...contextState.getData(), mapMode: false });
        useWingmanStore.getState().setSavedGalleryOpen(true);

        // 🛡️ ATOM V3: Emit system-level event
        spatialEventBus.emit('VAULT_ACCESS', {
            timestamp: Date.now(),
            itemsCount: useWingmanStore.getState().savedVideos.length
        });

        ForensicLogger.log('vault_access', {
            timestamp: Date.now(),
            savedItemsCount: useWingmanStore.getState().savedVideos.length
        });
    },

    closeVault() {
        telemetry.info('CONTEXT_ENGINE', 'VAULT_EXIT_TRIGGERED');
        contextState.setSystemMode('idle');
        contextState.setPanelMode('none');
        useWingmanStore.getState().setSavedGalleryOpen(false);
    },

    triggerClarification(message?: string) {
        const msg = message || "Can you clarify what location you're looking for?";
        telemetry.warn('CONTEXT_ENGINE', 'CLARIFICATION_REQUIRED', { message: msg });

        // Push the clarification request back to the UI/Voice layer
        contextState.setData({
            ...contextState.getData(),
            lastClarification: msg,
            reasoningThought: "I need more information to proceed with this spatial query."
        });

        contextState.setReasoningState('idle');

        if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.speak) {
            (window as any).WingmanSpatialControl.speak(msg);
        }
    },

    async handleExplicitReportRequest(
        transcript: string,
        classification: any,
        source: 'USER' | 'VOICE',
        cid: string
    ): Promise<any> {
        const store = useWingmanStore.getState();
        const activeContext = store.activeSpatialContext;
        const currentUserId = (typeof window !== 'undefined' && (window as any).WingmanSessionId) || 'user_default';

        // 1. Confirm-First Report Coordinator Evaluation
        const action = await reportConfirmationCoordinator.evaluateUtterance(transcript, {
            activeContext,
            userAuthId: currentUserId
        });

        if (action.type === 'CLARIFY') {
            store.setReportStatus('clarifying');
            if (source === 'VOICE' && typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.speak) {
                (window as any).WingmanSpatialControl.speak(action.message);
            }
            return {
                status: 'CLARIFICATION',
                message: action.message,
                reply: action.message
            };
        }

        if (action.type === 'BRIEF_PENDING') {
            store.setPendingReportBrief(action.brief!);
            store.setReportStatus('brief_pending');
            contextState.setPanelMode('news');
            if (source === 'VOICE' && typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.speak) {
                (window as any).WingmanSpatialControl.speak(action.message);
            }
            return {
                status: 'BRIEF_PENDING',
                message: action.message,
                reply: action.message,
                brief: action.brief
            };
        }

        if (action.type === 'ALLOWANCE_EXHAUSTED') {
            store.setReportStatus('allowance_exhausted');
            if (source === 'VOICE' && typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.speak) {
                (window as any).WingmanSpatialControl.speak(action.message);
            }
            return {
                status: 'ALLOWANCE_EXHAUSTED',
                message: action.message,
                reply: action.message
            };
        }

        if (action.type === 'CANCELLED') {
            store.setPendingReportBrief(null);
            store.setReportStatus('cancelled');
            return {
                status: 'CANCELLED',
                message: action.message,
                reply: action.message
            };
        }

        if (action.type === 'CONFIRM' || action.type === 'DISPATCH_CONFIRMED') {
            const confirmedBrief = action.brief || store.pendingReportBrief;
            if (!confirmedBrief) {
                return { status: 'ERROR', message: 'No active brief to confirm' };
            }
            store.setReportStatus('generating');
            store.setReportError(null);
            contextState.setPanelMode('news');

            try {
                // Confirm brief via coordinator to enforce version binding, allowance deduction & idempotency
                const confirmRes = await reportConfirmationCoordinator.confirmBrief(
                    confirmedBrief.briefId,
                    confirmedBrief.version,
                    currentUserId,
                    async (b) => {
                        return generateMockCanonicalReport(b);
                    }
                );

                if (!confirmRes.success) {
                    if (confirmRes.status === 'allowance_exhausted') {
                        store.setReportStatus('allowance_exhausted');
                        return { status: 'ALLOWANCE_EXHAUSTED', message: 'Report allowance exhausted.' };
                    }
                    store.setReportStatus('failed');
                    store.setReportError(confirmRes.error || 'Confirmation failed');
                    return { status: 'ERROR', message: confirmRes.error || 'Confirmation failed' };
                }

                // Generate canonical offline intelligence report
                const report = generateMockCanonicalReport(confirmedBrief);
                store.setActiveReport(report);
                store.setReportStatus('ready');

                const confirmMsg = `I have compiled an intelligence report for ${confirmedBrief.topic}. The briefing is ready in Spatial Command.`;
                reportVoiceLifecycle.acknowledgeCompletion(report, confirmedBrief.topic);

                return {
                    status: 'OK',
                    intent: 'report_generated',
                    message: confirmMsg,
                    reply: confirmMsg,
                    report
                };
            } catch (err: any) {
                store.setReportStatus('failed');
                store.setReportError(err.message || 'Report generation failed');
                return {
                    status: 'ERROR',
                    message: `Unable to compile intelligence report for ${confirmedBrief.topic}: ${err.message}`
                };
            }
        }

        return {
            status: 'IGNORED',
            message: 'Report request could not be processed.'
        };
    },

    async processUserMessage(rawTranscript: string, source: 'USER' | 'VOICE' = 'USER', correlationOrTurnId?: string, sessionId?: string): Promise<any> {
        // 🛡️ Request authority lock immediately to protect the reasoning/execution loop
        spatialEventBus.requestLock(source as any);

        const startTime = Date.now();
        const transcript = sanitizeInput(rawTranscript);
        const lowerTranscript = transcript.toLowerCase();

        // Phase 10.5: Set Correlation ID for session tracking
        const cid = correlationOrTurnId || `Q-${Math.floor(Math.random() * 90000) + 10000}`;
        console.log(`[FORENSIC] INPUT_RECEIVED: "${transcript}" (CID: ${cid})`);
        telemetry.log({
            subsystem: 'CONTEXT_ENGINE',
            event: 'INPUT_RECEIVED',
            level: 'FORENSIC',
            correlationId: cid,
            payload: { transcript }
        });
        traceStore.setCorrelationId(cid);
        WingmanAnalytics.search(transcript, 'chat');

        // 🛡️ GUARD: Handle undefined/null/empty input before any string operations
        if (transcript === undefined || transcript === null || transcript.trim().length === 0) {
            console.error('[FORENSIC] FAIL: undefined or empty input', { transcript: rawTranscript });
            telemetry.error('CONTEXT_ENGINE', 'EMPTY_INPUT', { transcript: rawTranscript });
            return { status: 'ERROR', reason: 'EMPTY_INPUT' };
        }

        try {
            // 🛡️ EXPLICIT REPORT INTENT & CONFIRM-FIRST FLOW INTERCEPT
            const reportClassifier = new ReportIntentClassifier();
            const reportClassification = reportClassifier.classifyIntent(transcript);
            if (reportClassification.isReportIntent || reportConfirmationCoordinator.isReportConversationActive()) {
                return await this.handleExplicitReportRequest(transcript, reportClassification, source, cid);
            }

            // 🛡️ ATOM S2: Unified Intent Triggering
            // We handle simple UI triggers directly to avoid the duplicate classifier file.
            let resolvedIntent = 'processing';

            // 🛡️ WINGMAN RADIO PLAYBACK VOICE INTENT INTERCEPT (WINGMAN-RADIO-010)
            const { radioVoiceController } = await import('../workspaces/radio/services/radioVoiceController');
            if (radioVoiceController.isRadioPlaybackIntent(transcript)) {
                const resolvedSessionId = sessionId || this.activeVoiceSessionId || (this as any).currentSessionId || ('transcript-session-' + cid);
                const radioResult = await radioVoiceController.handleMusicCommand(transcript, { turnId: cid, sessionId: resolvedSessionId });
                resolvedIntent = `radio_${radioResult.action}`;
                contextState.setIntent(resolvedIntent);
                return {
                    status: radioResult.success ? 'OK' : 'ERROR',
                    type: 'RADIO_PLAYBACK',
                    intent: resolvedIntent,
                    message: radioResult.message,
                    station: radioResult.station,
                    playbackState: radioResult.playbackState,
                    requiresGesture: radioResult.requiresGesture,
                    alternativeOffered: radioResult.alternativeOffered
                };
            }

            // 🛡️ PLATFORM WORKSPACE NAVIGATION INTENTS (Authoritative Single Source of Truth)
            const matchedWorkspace = workspaceAuthority.resolveWorkspace(lowerTranscript);
            const isWorkspaceNavIntent =
                lowerTranscript.includes("pull up") ||
                lowerTranscript.includes("open ") ||
                lowerTranscript.includes("switch to") ||
                lowerTranscript.includes("show me") ||
                lowerTranscript.includes("show ") ||
                lowerTranscript.includes("back to") ||
                lowerTranscript.includes("return to") ||
                lowerTranscript.includes("exit ") ||
                lowerTranscript.includes("leave ") ||
                lowerTranscript.includes("close ") ||
                lowerTranscript.includes("flight deals") ||
                lowerTranscript.includes("flight showroom") ||
                lowerTranscript.includes("real estate") ||
                lowerTranscript.includes("flight concierge") ||
                lowerTranscript.includes("write studio") ||
                lowerTranscript.includes("writing studio") ||
                lowerTranscript.includes("wingman workspace") ||
                lowerTranscript.includes("voice studio") ||
                lowerTranscript.includes("radio");

            if (matchedWorkspace && isWorkspaceNavIntent) {
                const navResult = workspaceAuthority.pullUpWorkspace(matchedWorkspace.id);
                resolvedIntent = matchedWorkspace.id === 'flight-showroom'
                    ? 'open_flight_showroom'
                    : matchedWorkspace.id === 'cockpit'
                        ? 'close_flight_showroom'
                        : `open_${matchedWorkspace.id}`;
                contextState.setIntent(resolvedIntent);
                return {
                    status: 'OK',
                    type: 'WORKSPACE_ACTION',
                    workspace: matchedWorkspace.id,
                    intent: resolvedIntent,
                    message: navResult.message
                };
            }

            // 🛡️ SHOWROOM WORKSPACE CONTEXT-AWARE COMMAND ROUTING
            if (workspaceRegistry.getActiveWorkspaceId() === 'flight-showroom' || contextState.getData()?.workspace === 'flight-showroom') {
                if (lowerTranscript.includes("tenerife")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { destination: 'Tenerife' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', destination: 'Tenerife' };
                }
                if (lowerTranscript.includes("reykjavik") || lowerTranscript.includes("iceland")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { destination: 'Reykjavik' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', destination: 'Reykjavik' };
                }
                if (lowerTranscript.includes("new york") || lowerTranscript.includes("nyc") || lowerTranscript.includes("jfk")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { destination: 'New York' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', destination: 'New York' };
                }
                if (lowerTranscript.includes("venice")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { destination: 'Venice' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', destination: 'Venice' };
                }
                if (lowerTranscript.includes("agadir") || lowerTranscript.includes("morocco")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { destination: 'Agadir' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', destination: 'Agadir' };
                }
                if (lowerTranscript.includes("cape verde") || lowerTranscript.includes("vicente")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { destination: 'São Vicente' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', destination: 'São Vicente' };
                }
                if (lowerTranscript.includes("beach")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { category: 'beach' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', category: 'beach' };
                }
                if (lowerTranscript.includes("europe")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { category: 'europe' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', category: 'europe' };
                }
                if (lowerTranscript.includes("nonstop") || lowerTranscript.includes("non-stop") || lowerTranscript.includes("direct")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { category: 'nonstop' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', category: 'nonstop' };
                }
                if (lowerTranscript.includes("budget") || lowerTranscript.includes("under 200") || lowerTranscript.includes("cheap")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { category: 'budget' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', category: 'budget' };
                }
                if (lowerTranscript.includes("savings") || lowerTranscript.includes("best deal")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { category: 'savings' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', category: 'savings' };
                }
                if (lowerTranscript.includes("fastest")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { flightTier: 'fastest' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', flightTier: 'fastest' };
                }
                if (lowerTranscript.includes("best value")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { flightTier: 'best_value' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', flightTier: 'best_value' };
                }
                if (lowerTranscript.includes("flexible")) {
                    window.dispatchEvent(new CustomEvent('SHOWROOM_VOICE_COMMAND', { detail: { flightTier: 'flexible' } }));
                    return { status: 'OK', type: 'SHOWROOM_COMMAND', flightTier: 'flexible' };
                }
            }

            // 🛡️ TRIPADVISOR REVIEWS CONTEXT-AWARE INTELLIGENCE
            const isReviewQuery =
                lowerTranscript.includes("tripadvisor") ||
                lowerTranscript.includes("what are guests saying") ||
                lowerTranscript.includes("guest reviews") ||
                lowerTranscript.includes("how is the service rated") ||
                lowerTranscript.includes("good for families") ||
                lowerTranscript.includes("hotel reviews") ||
                (lowerTranscript.includes("reviews") && (lowerTranscript.includes("hotel") || lowerTranscript.includes("place") || lowerTranscript.includes("stay")));

            if (isReviewQuery) {
                const taContext = contextState.getTripadvisorContext?.();
                if (!taContext || !taContext.placeName) {
                    return {
                        status: 'OK',
                        type: 'TRIPADVISOR_ANSWER',
                        intent: 'tripadvisor_reviews',
                        message: "No TripAdvisor reviews have been loaded for this property yet. Open TripAdvisor Reviews in the Flight Showroom to view verified traveler ratings."
                    };
                }

                // Service rating query
                if (lowerTranscript.includes("service")) {
                    const serviceRating = taContext.categoryRatings?.Service ?? taContext.categoryRatings?.service;
                    const ratingMsg = serviceRating
                        ? `On TripAdvisor, service at ${taContext.placeName} is rated ${serviceRating} out of 5 stars.`
                        : `A specific service rating is unavailable in the retained TripAdvisor evidence for ${taContext.placeName}.`;
                    return {
                        status: 'OK',
                        type: 'TRIPADVISOR_ANSWER',
                        intent: 'tripadvisor_service_rating',
                        message: ratingMsg
                    };
                }

                // Family query
                if (lowerTranscript.includes("famil")) {
                    const familyReviews = (taContext.topReviews || []).filter(r => r.type?.toUpperCase() === 'FAMILY' || r.snippet.toLowerCase().includes('famil'));
                    const familyMsg = familyReviews.length > 0
                        ? `One returned TripAdvisor family review for ${taContext.placeName} says: "${familyReviews[0].snippet.slice(0, 140)}" (Rating: ${familyReviews[0].rating}★). This is limited review evidence, not a general family-suitability verdict.`
                        : `The retained TripAdvisor evidence for ${taContext.placeName} has no specific family-traveler evidence, so family suitability cannot be determined.`;
                    return {
                        status: 'OK',
                        type: 'TRIPADVISOR_ANSWER',
                        intent: 'tripadvisor_family_sentiment',
                        message: familyMsg
                    };
                }

                // General guest sentiment query
                const topSnippet = (taContext.topReviews && taContext.topReviews.length > 0)
                    ? ` A returned traveler review says: "${taContext.topReviews[0].snippet.slice(0, 140)}"`
                    : '';
                const aggregate = taContext.averageRating !== undefined
                    ? `TripAdvisor's provider rating for ${taContext.placeName} is ${taContext.averageRating} out of 5${taContext.totalReviews !== undefined ? ` across ${taContext.totalReviews.toLocaleString()} reviews` : ''}.`
                    : `An overall TripAdvisor rating is unavailable in the retained evidence for ${taContext.placeName}.`;
                return {
                    status: 'OK',
                    type: 'TRIPADVISOR_ANSWER',
                    intent: 'tripadvisor_guest_sentiment',
                    message: `${aggregate}${topSnippet}`
                };
            }

            if (lowerTranscript.includes("settings")) {
                contextState.setPanelMode("settings");
                resolvedIntent = "open_settings";
                return { status: 'OK', type: 'UI_ACTION', intent: resolvedIntent };
            } else if (
                lowerTranscript.includes("pricing") ||
                lowerTranscript.includes("upgrade") ||
                lowerTranscript.includes("cost") ||
                lowerTranscript.includes("subscription") ||
                /\b(show|see|view|open|what)\b.*\bplans?\b/.test(lowerTranscript)
            ) {
                contextState.setPanelMode("pricing");
                resolvedIntent = "open_pricing";
                contextState.setIntent(resolvedIntent);
                return { status: 'OK', type: 'UI_ACTION', intent: resolvedIntent };
            } else if (
                contextState.getQuotaState?.()?.exhausted &&
                /\b(why|limit|quota|continue|stopped|blocked|can't|cannot)\b/.test(lowerTranscript)
            ) {
                return {
                    status: 'OK',
                    type: 'QUOTA_GUIDANCE',
                    intent: 'explain_quota',
                    message: formatQuotaGuidance(contextState.getQuotaState?.() || null)
                };
            } else if (lowerTranscript.includes("chat")) {
                contextState.setPanelMode("chat");
                resolvedIntent = "open_chat";
                return { status: 'OK', type: 'UI_ACTION', intent: resolvedIntent };
            } else if (lowerTranscript.includes("map")) {
                contextState.setPanelMode("map");
                resolvedIntent = "show_map";
            }

            contextState.setIntent(resolvedIntent);
            traceRecorder.recordAIIntent(resolvedIntent, transcript);

            const intentEngine = new IntentEngine();
            const intent = await intentEngine.processInput(transcript);

            // 🛡️ CRITICAL GUARD: Low confidence triggers disambiguation/clarification
            if (intent.confidence < 0.6) {
                ForensicLogger.log('intent_decision', {
                    intent: intent.intent,
                    confidence: intent.confidence,
                    action: intent.action,
                    query: intent.query,
                    status: 'CLARIFICATION_TRIGGERED'
                });
                this.triggerClarification(intent.message);
                return { status: 'CLARIFICATION', message: intent.message };
            }

            // 🔍 TRACE INTENT DECISION
            ForensicLogger.log('intent_decision', {
                intent: intent.intent,
                confidence: intent.confidence,
                action: intent.action,
                query: intent.query
            });

            const isSpatialQuery = intent.intent !== 'NON_SPATIAL';

            // 🔒 IAM-M16A: Location Lock Command Interception (Prevents GPS snap-back)
            if (
                lowerTranscript === 'lock the map' ||
                lowerTranscript === 'lock map' ||
                lowerTranscript === 'lock location' ||
                lowerTranscript === 'lock my location' ||
                lowerTranscript === 'stay here' ||
                lowerTranscript === 'lock'
            ) {
                telemetry.info('CONTEXT_ENGINE', 'LOCATION_LOCK_COMMAND_TRIGGERED', { locked: true });
                if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.setLocationLock) {
                    (window as any).WingmanSpatialControl.setLocationLock(true);
                }
                const msg = 'Map location is now locked. Live GPS updates will not move the map.';
                if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.speak) {
                    (window as any).WingmanSpatialControl.speak(msg);
                }
                return { status: 'OK', message: msg, type: 'LOCATION_LOCKED' };
            }

            if (
                lowerTranscript === 'unlock the map' ||
                lowerTranscript === 'unlock map' ||
                lowerTranscript === 'unlock location' ||
                lowerTranscript === 'unlock my location' ||
                lowerTranscript === 'resume tracking' ||
                lowerTranscript === 'unlock'
            ) {
                telemetry.info('CONTEXT_ENGINE', 'LOCATION_LOCK_COMMAND_TRIGGERED', { locked: false });
                if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.setLocationLock) {
                    (window as any).WingmanSpatialControl.setLocationLock(false);
                }
                const msg = 'Map location unlocked. Live GPS tracking resumed.';
                if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.speak) {
                    (window as any).WingmanSpatialControl.speak(msg);
                }
                return { status: 'OK', message: msg, type: 'LOCATION_UNLOCKED' };
            }

            if (isSpatialQuery) {
                telemetry.info('CONTEXT_ENGINE', 'SPATIAL_QUERY_INTERCEPTED');

                // Task 2: Explicit Globe View Override
                if (lowerTranscript === "show me the map") {
                    telemetry.info('CONTEXT_ENGINE', 'GLOBE_VIEW_TRIGGERED');
                    contextState.setState('navigation', {
                        center: { lat: WORLD_VIEW.lat, lon: WORLD_VIEW.lon },
                        zoom: WORLD_VIEW.zoom,
                        mapMode: true
                    }, 'map');
                    return { status: 'OK', type: 'GLOBE_VIEW' };
                }

                // 🧭 WINGMAN INDOOR ROUTING: AIRPORT WAYFINDING LOCAL FAST PATH (IAM-M16B)
                // Authoritative indoor route calculation and visualization for active venues
                const indoorRoutePattern = /^(?:route\s+(?:from\s+)?|directions\s+(?:from\s+)?|navigate\s+(?:from\s+)?|wayfinding\s+(?:from\s+)?|take\s+me\s+from\s+|from\s+)?(.+?)\s+(?:to|->|→)\s+(.+)$/i;
                const indoorRouteMatch = transcript.trim().match(indoorRoutePattern);

                if (indoorRouteMatch) {
                    const rawOrigin = indoorRouteMatch[1].trim().replace(/^["'“”‘’]+|["'“”‘’]+$/g, '').replace(/[.,!?;:]+$/, '').trim();
                    const rawDest = indoorRouteMatch[2].trim().replace(/^["'“”‘’]+|["'“”‘’]+$/g, '').replace(/[.,!?;:]+$/, '').trim();
                    const isFlyCommand = /^(?:fly|go|head|take me)$/i.test(rawOrigin);

                    // Check if this is an indoor wayfinding query (either active airport venue OR contains indoor keywords)
                    const hasIndoorKeywords = /\b(security|tsa|gate|terminal|concourse|baggage|check-in|checkin|restroom|lounge|b20|b32)\b/i.test(transcript);
                    const isAirportActive = Boolean(indoorMapController.getActiveAirport());

                    if (!isFlyCommand && rawOrigin.length >= 2 && rawDest.length >= 2 && (hasIndoorKeywords || isAirportActive)) {
                        telemetry.info('CONTEXT_ENGINE', 'INDOOR_ROUTE_REQUEST_INTERCEPTED', {
                            origin: rawOrigin,
                            destination: rawDest,
                            airport: indoorMapController.getActiveAirport()
                        });

                        const routeResult = indoorMapController.calculateAndRenderRoute(rawOrigin, rawDest);

                        if (routeResult.status === 'ROUTE_FOUND' && routeResult.route?.success) {
                            const dist = routeResult.route.totalDistanceMeters;
                            const levels = routeResult.route.levelsTraversed;
                            const transitions = routeResult.route.levelTransitions;
                            const navMsg = `Indoor route displayed from ${rawOrigin} to ${rawDest}. Distance is ${dist.toFixed(1)}m${transitions > 0 ? ` with ${transitions} floor transition` : ''}.`;

                            if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.speak) {
                                (window as any).WingmanSpatialControl.speak(navMsg);
                            }

                            contextState.setPanelMode('map');
                            contextState.setRoutingState('ACTIVE');

                            telemetry.info('CONTEXT_ENGINE', 'INDOOR_ROUTE_SUCCESS', {
                                origin: rawOrigin,
                                destination: rawDest,
                                distance: dist,
                                transitions,
                                levels
                            });

                            return {
                                status: 'OK',
                                type: 'INDOOR_ROUTE',
                                message: navMsg,
                                data: {
                                    origin: rawOrigin,
                                    destination: rawDest,
                                    distanceMeters: dist,
                                    nodesCount: routeResult.route.path.length,
                                    transitionsCount: transitions,
                                    levelsTraversed: levels,
                                    route: routeResult.route,
                                    visualization: routeResult.visualization
                                }
                            };
                        } else {
                            // FAIL CLOSED — per Real-Data Firewall: NEVER fabricate straight line or fake route
                            telemetry.warn('CONTEXT_ENGINE', 'INDOOR_ROUTE_FAILED_CLOSED', {
                                status: routeResult.status,
                                error: routeResult.error,
                                origin: rawOrigin,
                                destination: rawDest
                            });

                            const failMsg = `Indoor route from ${rawOrigin} to ${rawDest} unavailable: ${routeResult.status}`;
                            return {
                                status: 'FAIL_CLOSED',
                                type: 'INDOOR_ROUTE_NOT_AVAILABLE',
                                message: failMsg,
                                data: {
                                    status: routeResult.status,
                                    error: routeResult.error,
                                    origin: rawOrigin,
                                    destination: rawDest
                                }
                            };
                        }
                    }
                }

                // ✈️ WINGMAN AIRPORT MODULE: R1-FINAL LOCAL AIRPORT FAST PATH
                // Invariant 1: Navigation must not depend on conversational enrichment
                // Invariant 2: Network failure != camera movement
                const isNavigationalRequest =
                    intent.intent === 'NAVIGATION' ||
                    /^(?:fly\s+to|take\s+me\s+to|navigate\s+to|go\s+to|head\s+to|fly\s+into|bring\s+me\s+to)\b/i.test(transcript.trim());

                const isPureInformational =
                    /^(?:tell\s+me\s+about|what(?:'s|\s+is)\s+happening|what\s+is\s+the\s+weather|weather\s+at|news\s+about|how\s+is)\b/i.test(transcript.trim());

                const cleanQuery = transcript.trim().toLowerCase().replace(/^(?:fly\s+to|take\s+me\s+to|navigate\s+to|go\s+to|head\s+to|fly\s+into|bring\s+me\s+to)\s+/i, '').replace(/[?.,!]/g, '').trim();

                const isExplicitAirportQuery = 
                    /\b(airport|terminal|airfield|intl|international)\b/i.test(transcript) ||
                    cleanQuery.split(/\s+/).some(token => INDOOR_AIRPORTS[token.toUpperCase()]) ||
                    Boolean(AIRPORT_ALIASES[cleanQuery]) ||
                    Boolean(INDOOR_AIRPORTS[cleanQuery.toUpperCase()]);

                if (isNavigationalRequest && !isPureInformational && isExplicitAirportQuery) {
                    const airport = indoorMapController.resolveAirportFromQuery(transcript);
                    if (airport) {
                        telemetry.info('CONTEXT_ENGINE', 'LOCAL_AIRPORT_FAST_PATH_TRIGGERED', {
                            iata: airport.iata,
                            name: airport.name,
                            center: airport.center
                        });

                        // 1. Authoritative Navigation Execution (Contract 3: FlyTo)
                        indoorMapController.flyToAirportIndoor(airport.iata);

                        // 2. Lock Location to prevent live GPS updates from snapping map away
                        if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.setLocationLock) {
                            (window as any).WingmanSpatialControl.setLocationLock(true);
                        }

                        // 3. Audio/Voice state transition
                        voiceState.set('flying');

                        // 4. Update HUD and Zustand spatial context
                        const store = useWingmanStore.getState();
                        store.setActiveLocationName(airport.name.toUpperCase());
                        store.setLocationQuery(transcript);
                        store.setTargetLocation([airport.center[0], airport.center[1]]);
                        const scope = new ReportIntentClassifier().resolveScope(airport.name, 'United States');
                        store.setActiveSpatialContext({
                            name: airport.name,
                            latitude: airport.center[1],
                            longitude: airport.center[0],
                            country: 'United States',
                            scope: scope,
                            updatedAt: new Date().toISOString()
                        });

                        // 5. Update central context state
                        contextState.setPanelMode('map');
                        contextState.setState('selectedLocation', {
                            center: { lat: airport.center[1], lon: airport.center[0] },
                            zoom: airport.defaultZoom,
                            lat: airport.center[1],
                            lon: airport.center[0],
                            name: airport.name,
                            address: `${airport.name}, ${airport.iata}`,
                            mapMode: true,
                            intent: 'jet'
                        }, 'map');
                        contextState.setSpatialContext?.({
                            lat: airport.center[1],
                            lon: airport.center[0],
                            zoom: airport.defaultZoom,
                            pitch: airport.defaultPitch,
                            bearing: airport.defaultBearing
                        });
                        contextState.setRoutingState('ACTIVE');

                        const navMsg = `Navigating to ${airport.name} (${airport.iata}).`;
                        if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.speak) {
                            (window as any).WingmanSpatialControl.speak(navMsg);
                        }

                        // 6. Asynchronous Conversational Enrichment (Phase 4: Enrichment Separation)
                        // Remote intelligence enriches navigation in background, but failure NEVER moves camera or undoes navigation.
                        (async () => {
                            try {
                                const isAlive = GatewayHealthService.getState().online;
                                if (!isAlive) return;
                                const gatewayUrl = (import.meta as any).env.VITE_GATEWAY_URL;
                                if (!gatewayUrl) return;

                                const response = await authenticatedFetch(`${gatewayUrl}/wingman/query`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        query: transcript,
                                        context: {
                                            location: { lat: airport.center[1], lon: airport.center[0] },
                                            uiContext: {
                                                activeMode: 'DEFAULT',
                                                panelMode: 'map',
                                                userTier: useWingmanStore.getState().userTier,
                                                isLocked: true
                                            }
                                        }
                                    }),
                                    signal: AbortSignal.timeout(10000)
                                });

                                if (response.ok) {
                                    const enrichResult = await response.json();
                                    if (enrichResult.status === 'OK' && enrichResult.data) {
                                        if (enrichResult.data.markers) {
                                            store.setRecommendations(enrichResult.data.markers);
                                        }
                                        if (enrichResult.data.areaAnalysis) {
                                            store.setAreaAnalysis(enrichResult.data.areaAnalysis);
                                        }
                                    }
                                }
                            } catch (enrichErr: any) {
                                telemetry.warn('CONTEXT_ENGINE', 'AIRPORT_ENRICHMENT_FAILED_GRACEFULLY', { error: enrichErr?.message });
                            }
                        })();

                        return {
                            status: 'OK',
                            type: 'AIRPORT_NAVIGATION',
                            message: navMsg,
                            data: {
                                airport,
                                center: { lat: airport.center[1], lon: airport.center[0] },
                                location: airport.center,
                                zoom: airport.defaultZoom,
                                name: airport.name,
                                iata: airport.iata
                            }
                        };
                    }
                }

                if (lowerTranscript.includes("booth")) {
                    contextState.setPanelMode("booth");
                    contextState.setData({ ...contextState.getData(), environmentHint: "booth" });
                    environmentController.setEnvironment("booth");
                    return { status: 'OK', type: 'BOOTH_VIEW' };
                }

                // Phase 11: Copilot Planning for high-level requests
                if (lowerTranscript.includes("plan") || lowerTranscript.includes("itinerary") || lowerTranscript.includes("guide")) {
                    copilotEngine.startNewPlan(resolvedIntent, transcript);
                }

                let detectedIntent = "penthouse"; // fallback for general maps
                if (lowerTranscript.includes("beach")) detectedIntent = "beach";
                else if (lowerTranscript.includes("yacht") || lowerTranscript.includes("boat")) detectedIntent = "yacht";
                else if (lowerTranscript.includes("restaurant") || lowerTranscript.includes("lounge") || lowerTranscript.includes("rooftop") || lowerTranscript.includes("club")) detectedIntent = "lounge";
                else if (lowerTranscript.includes("flight") || lowerTranscript.includes("airport") || lowerTranscript.includes("jet")) detectedIntent = "jet";

                // 🥈 FIX: Force BOTH maps on (Panel + Background)
                contextState.setPanelMode("map");
                contextState.setData({
                    ...contextState.getData(),
                    mapMode: true,
                    intent: detectedIntent
                });

                if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl && (window as any).WingmanSpatialControl.setIntent) {
                    (window as any).WingmanSpatialControl.setIntent(detectedIntent);
                }
            }

            // Phase 6.5: Resolve browser location (with a failsafe timeout)
            let browserLocation = null;
            try {
                // Race the geolocation against a 5s timeout for accuracy
                browserLocation = await Promise.race([
                    getBrowserLocation(),
                    new Promise(resolve => setTimeout(() => resolve(null), 5000))
                ]) as { lat: number, lon: number } | null;
            } catch (e) {
                telemetry.warn('CONTEXT_ENGINE', 'GEOLOCATION_TIMEOUT');
            }

            // Phase 4: Readiness Check
            let isAlive = GatewayHealthService.getState().online;
            if (!isAlive) {
                try {
                    const { checkGatewayHealth } = await import('../services/gateway/gatewayHealth');
                    isAlive = await checkGatewayHealth();
                } catch (e) {
                    isAlive = false;
                }
            }

            if (isAlive) {
                contextState.setConnectionStatus('online');
            } else {
                contextState.setConnectionStatus('offline');
                telemetry.warn('CONTEXT_ENGINE', 'GATEWAY_CHECK_FAILED', { action: 'FALLING_BACK_TO_LOCAL' });
            }

            // AI Agent Reasoning Loop (Agentic System)
            const getTimestamp = () => new Date().toLocaleTimeString('en-US', { hour12: false });
            let isWebResearch = false;
            let requestId = '';

            try {
                if (!isAlive) {
                    throw new Error("Wingman Gateway Offline");
                }

                contextState.setConnectionStatus('active');
                contextState.setReasoningState('planning');

                // 🛡️ GLOBAL SAFETY TIMEOUT: Force reset if stuck
                setTimeout(() => {
                    const current = contextState.getReasoningState();
                    if (current === 'planning') {
                        telemetry.warn('CONTEXT_ENGINE', 'FORCE_RESET_STUCK_PLANNING');
                        contextState.setReasoningState('idle');
                    }
                }, 30000); // 30 seconds (Fetch timeout is 25s)

                // 🧠 NAVIGATION STATE: Transition to PLANNING
                if (typeof window !== 'undefined' && (window as any).WingmanStateMachine) {
                    (window as any).WingmanStateMachine.transition("PLANNING", "AI");
                }

                const userLocation = browserLocation || WORLD_VIEW;

                // MISSION: Sync UI Mode from Controller
                const activeMode = typeof window !== 'undefined' && (window as any).ModeController?.getActiveMode() || 'DEFAULT';
                contextState.setActiveMode(activeMode);

                // 🛡️ STRICT ROUTING: Use the validated gateway URL. 
                const gatewayUrl = (import.meta as any).env.VITE_GATEWAY_URL;
                const isProd = (import.meta as any).env.VITE_PROD_MODE === 'true';

                telemetry.info('CONTEXT_ENGINE', 'DISPATCHING_QUERY', { url: `${gatewayUrl}/wingman/query`, target: isProd ? 'CLOUD' : 'LOCAL' });

                // Dynamic timeout: 120s for live web research, 25s for normal chat
                const researchResolver = new ResearchIntentResolver();
                isWebResearch = researchResolver.isWebResearchRequested(transcript);
                const requestTimeoutMs = isWebResearch ? 120000 : 25000;
                requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

                if (isWebResearch) {
                    ResearchActivityController.startResearch(transcript, requestId);
                    ResearchActivityController.updatePhase(requestId, 'SOURCE_RESOLVED', 'Connecting to live sources');
                } else {
                    ResearchActivityController.resetState();
                }

                const controller = new AbortController();
                const timeoutId = setTimeout(() => {
                    controller.abort(new DOMException('Web research query timed out', 'TimeoutError'));
                }, requestTimeoutMs);

                let response;
                try {
                    response = await authenticatedFetch(`${gatewayUrl}/wingman/query`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            query: transcript,
                            context: {
                                location: userLocation,
                                uiContext: {
                                    activeMode: activeMode,
                                    panelMode: contextState.getPanelMode(),
                                    userTier: useWingmanStore.getState().userTier,
                                    isLocked: (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.getLocationLock?.()) || false
                                },
                                sessionId: (typeof window !== 'undefined' && (window as any).WingmanSessionId)
                                    ? (window as any).WingmanSessionId
                                    : (typeof localStorage !== 'undefined' && localStorage.getItem('wingman_session_id'))
                                        ? localStorage.getItem('wingman_session_id')
                                        : (() => {
                                            const newId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
                                            if (typeof window !== 'undefined') (window as any).WingmanSessionId = newId;
                                            if (typeof localStorage !== 'undefined') localStorage.setItem('wingman_session_id', newId);
                                            return newId;
                                        })(),
                                ...buildRealEstateRequestContext(),
                                ...buildFlightRequestContext(),
                                ...buildRadioRequestContext(),
                            }
                        }),
                        signal: controller.signal
                    });

                    if (isWebResearch && response.ok) {
                        ResearchActivityController.updatePhase(requestId, 'SYNTHESIZING', 'Synthesizing report');
                    }
                } catch (fetchErr: any) {
                    if (isWebResearch) {
                        ResearchActivityController.failResearch(requestId, fetchErr?.message || fetchErr?.toString());
                    }
                    throw fetchErr;
                } finally {
                    clearTimeout(timeoutId);
                }

                // 🛡️ QUOTA CONCIERGE: Intercept 429 to trigger premium upgrade experience
                if (response.status === 429) {
                    const data = await response.json();
                    contextState.setReasoningState('idle');
                    const quotaData = {
                        ...(data.tier !== undefined ? { tier: data.tier } : {}),
                        ...(data.used !== undefined ? { used: data.used } : {}),
                        ...(data.limit !== undefined ? { limit: data.limit } : {}),
                        ...(data.resource !== undefined ? { resource: data.resource } : {}),
                        ...(data.category !== undefined ? { category: data.category } : {}),
                        ...(data.type !== undefined ? { type: data.type } : {}),
                        ...(data.quotaType !== undefined ? { quotaType: data.quotaType } : {}),
                        ...(data.feature !== undefined ? { feature: data.feature } : {}),
                        ...(data.message !== undefined ? { message: data.message } : {}),
                        latency: Date.now() - startTime,
                        timestamp: new Date().toISOString()
                    };
                    const combinedData = { ...data, ...quotaData };
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent("WINGMAN_QUOTA_EXCEEDED", {
                            detail: combinedData
                        }));
                    }
                    const quotaMsg = combinedData.message
                        || "You've reached the current limit for this feature. I can show you the available plans.";
                    // 🛡️ WINGMAN-VOICE-014: Routine counters and upgrade prompts remain strictly visual
                    return { status: 'QUOTA_EXCEEDED', message: quotaMsg, data: combinedData };
                }

                // 🛡️ HARDENING: Force fetch to fail on HTTP Error Codes!
                if (!response.ok) {
                    throw new Error(`Gateway returned HTTP ${response.status}`);
                }

                contextState.setReasoningState('executing');
                const result = await response.json();
                telemetry.log({ subsystem: 'CONTEXT_ENGINE', event: 'GATEWAY_RESPONSE', level: 'DEBUG', payload: result });

                if (result.status !== 'OK') {
                    const msg = result.message || 'Unknown gateway error';
                    console.error(`[Agent] Gateway error: ${msg}`);
                    throw new Error(`Gateway returned non-OK status: ${result.status} - ${msg}`);
                }

                // A successful authoritative gateway result supersedes prior quota exhaustion.
                contextState.clearQuotaState?.();

                // ── CACHE THE RESULT ─────────────────────────────────────────────
                // Store deterministic gateway responses for 5 minutes.
                // This eliminates redundant network requests for repeated queries
                // (e.g., "weather in Miami", "news in New York").
                setCachedDestination(transcript, result);
                // ──────────────────────────────────────────────────────────────────

                // 2️⃣ Optimized Orchestration: Use Pass-Through Data
                if (result.status === 'OK') {
                    const store = useWingmanStore.getState();
                    const markers = result.data?.markers || [];

                    // 🧠 WINGMAN LOCATION AUTHORITY ENGINE (TESTED MODE)
                    let backendCenter = null;
                    let userIntentCoords = null;

                    // Extract coordinates if present in transcript (Explicit User Intent)
                    const coordMatch = transcript.match(/(-?\d+\.\d+),\s*(-?\d+\.\d+)/);
                    if (coordMatch) {
                        userIntentCoords = { lat: parseFloat(coordMatch[1]), lon: parseFloat(coordMatch[2]) };
                    }

                    if (result.data?.center || result.data?.location) {
                        const c = result.data.center || result.data.location;
                        if (Array.isArray(c) && c.length === 2) {
                            const coerced = coerceCoordinates(c, 'americas');
                            if (coerced) {
                                backendCenter = { lon: coerced[0], lat: coerced[1] };
                            }
                        } else if (c && typeof c === 'object' && c.lat !== undefined) {
                            backendCenter = { lat: c.lat, lon: (c as any).lon || (c as any).lng };
                        }
                    }

                    // 🛡️ DETERMINISTIC RESOLUTION HIERARCHY
                    let finalCenter = null;
                    let sourceAuthority = 'UNKNOWN';

                    const isLocked = typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.getLocationLock?.();

                    // 1. LOCKED CONTEXT (SUPREME AUTHORITY)
                    if (isLocked && isValidCoordinate(contextState.getData()?.center?.lat, contextState.getData()?.center?.lon)) {
                        finalCenter = contextState.getData().center;
                        sourceAuthority = 'LOCKED_CONTEXT';
                    }

                    // 2. USER INTENT
                    if (!finalCenter && isValidCoordinate(userIntentCoords?.lat, userIntentCoords?.lon)) {
                        finalCenter = userIntentCoords;
                        sourceAuthority = 'USER';
                    }

                    // 3. BACKEND RESOLVED
                    if (!finalCenter && isValidCoordinate(backendCenter?.lat, backendCenter?.lon)) {
                        finalCenter = backendCenter;
                        sourceAuthority = 'BACKEND';
                    }

                    // 4. FRONTEND RESOLVER
                    if (!finalCenter && isSpatialQuery) {
                        try {
                            const resolver = new LocationResolver(
                                (import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY,
                                (import.meta as any).env.VITE_MAPBOX_TOKEN
                            );

                            const targetQuery = intent.query || transcript;
                            const locResult = await resolver.resolveLocation(targetQuery);

                            if (isValidCoordinate(locResult.coordinates[1], locResult.coordinates[0])) {
                                finalCenter = { lat: locResult.coordinates[1], lon: locResult.coordinates[0] };
                                sourceAuthority = 'LOCAL_RESOLVER';

                                // 🛰️ ATOM S3: Immediate HUD sync for local resolution
                                store.setActiveLocationName(targetQuery.toUpperCase());
                                store.setLocationQuery(targetQuery);
                                store.setTargetLocation([locResult.coordinates[0], locResult.coordinates[1]]);
                                const scope = new ReportIntentClassifier().resolveScope(targetQuery, locResult.country);
                                store.setActiveSpatialContext({
                                    name: targetQuery,
                                    latitude: locResult.coordinates[1],
                                    longitude: locResult.coordinates[0],
                                    country: locResult.country,
                                    scope: scope,
                                    updatedAt: new Date().toISOString()
                                });
                            }
                        } catch (e: any) { }
                    }

                    // 5. REAL GPS (Only fallback if NO spatial query was intended)
                    if (!finalCenter && !isSpatialQuery) {
                        if (isValidCoordinate(browserLocation?.lat, browserLocation?.lon)) {
                            finalCenter = browserLocation;
                            sourceAuthority = 'REAL_GPS';
                        }
                    }

                    // 4. PRESERVE EXISTING CAMERA STATE (Never force WORLD_VIEW on generic missing coords)
                    if (!finalCenter) {
                        const preserved = contextState.getData()?.center;
                        if (isValidCoordinate(preserved?.lat, preserved?.lon)) {
                            finalCenter = preserved;
                            sourceAuthority = 'PRESERVED_CURRENT_STATE';
                        } else {
                            finalCenter = WORLD_VIEW;
                            sourceAuthority = 'WORLD_VIEW';
                        }
                    }

                    assertNoDefaultLocation(finalCenter, transcript);

                    const center = finalCenter;

                    // Phase 7: Resolve bounds for region navigation
                    let resolvedBounds = null;
                    if (result.data?.bounds) {
                        const b = result.data.bounds;
                        if (Array.isArray(b) && b.length === 2 && Array.isArray(b[0]) && Array.isArray(b[1])) {
                            resolvedBounds = b;
                        }
                    }

                    const isNavigation = result.intent === 'navigate' || result.data?.action === 'NAVIGATE_TO_VENUE';

                    const newStateData = {
                        ...result.data,
                        markers: markers,
                        center: center,
                        bounds: resolvedBounds,
                        zoom: result.data?.zoom || null,
                        panelMode: isNavigation ? 'navigation' : (result.data?.panelMode || 'map'),
                        mapMode: true
                    };

                    contextState.setState(result.intent || result.data?.intent || "navigation", newStateData, 'map');

                    // ⏺️ RECORD FOR REPLAY
                    if (center) {
                        replaySystem.recordFrame(transcript, { lat: center.lat, lon: center.lon }, result.data?.zoom || 14);
                    }
                    contextState.setReasoningState('complete');

                    setTimeout(() => contextState.setReasoningState('idle'), 2000);

                    if (result.data?.environmentHint) {
                        environmentController.setEnvironment(result.data.environmentHint);
                    }

                    if (newStateData.panelMode) {
                        contextState.setPanelMode(newStateData.panelMode);
                    }

                    if (isNavigation && center) {
                        const backendLocName = result.data?.locationName || result.data?.locationQuery || "AI Selected Destination";
                        contextState.setState('selectedLocation', {
                            lat: center.lat,
                            lon: center.lon,
                            name: backendLocName,
                            address: result.data?.address || "Guided Route"
                        }, 'map');

                        contextState.setRoutingState('ACTIVE');
                        contextState.fetchRoute([center.lon, center.lat]);
                    }

                    // 🧠 ORCHESTRATION: Sync UI State
                    store.setConfidence(intent.intentConfidence, intent.locationConfidence || 0);
                    store.setRecommendations(markers);

                    // 🛡️ ATOM S1/S3: Sync location metadata to Zustand
                    const backendLocName = result.data?.locationName || result.data?.locationQuery;
                    if (backendLocName) {
                        store.setActiveLocationName(backendLocName.toUpperCase());
                        store.setLocationQuery(backendLocName);
                        if (center && isValidCoordinate(center.lat, center.lon)) {
                            store.setTargetLocation([center.lon, center.lat]);
                            const countryName = result.data?.country || (backendLocName.toLowerCase() === 'spain' ? 'Spain' : undefined);
                            const scope = new ReportIntentClassifier().resolveScope(backendLocName, countryName);
                            store.setActiveSpatialContext({
                                name: backendLocName,
                                latitude: center.lat,
                                longitude: center.lon,
                                country: countryName,
                                scope: scope,
                                updatedAt: new Date().toISOString()
                            });
                        }
                    }

                    if (result.data?.areaAnalysis) {
                        store.setAreaAnalysis(result.data.areaAnalysis);
                    }

                    // 🎬 VISUAL INTELLIGENCE
                    const hasVideos = result.data?.videos && result.data.videos.length > 0;
                    const isVideoInsight = result.intent === 'video_insight' || result.data?.action === 'VIDEO_INSIGHT' || hasVideos;

                    if (isVideoInsight) {
                        store.setInsight({
                            action: 'VIDEO_INSIGHT',
                            message: result.message || result.data?.summary || "Visual atmosphere analysis ready.",
                            priority: result.data?.priority || 2,
                            videos: result.data?.videos || [],
                            vibe: result.data?.insight?.vibe,
                            tags: result.data?.insight?.tags,
                            insightSummary: result.data?.insight?.summary
                        });
                    }

                    // 🚀 CAMERA BRIDGE (🛡️ ATOM S9.0.6: Intent Pipeline)
                    try {
                        if (center && isValidCoordinate(center.lat, center.lon)) {
                            const intentType = resolvedBounds ? 'FIT_BOUNDS' : 'NAVIGATE';
                            const payload: any = resolvedBounds
                                ? {
                                    bounds: resolvedBounds,
                                    intent: 'navigate',
                                    context: 'city',
                                    essential: true,
                                }
                                : {
                                    center: [center.lon, center.lat],
                                    zoom: result.data?.zoom || 14,
                                    pitch: 60,
                                    bearing: -20,
                                    intent: 'navigate',
                                    essential: true,
                                };

                            const latency = Date.now() - startTime;
                            const bus = getSafeEventBus();
                            const intentPacket = {
                                intentId: cid,
                                intentType: intentType,
                                source: source as any,
                                payload: payload,
                                timestamp: Date.now(),
                                latencyMs: latency
                            };

                            if (bus) {
                                voiceState.set('flying');
                                // 🔒 IAM-M16A: Auto-lock location on flight navigation so background GPS does not snap map back
                                if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.setLocationLock) {
                                    (window as any).WingmanSpatialControl.setLocationLock(true);
                                }
                                bus.emit('SPATIAL_INTENT', intentPacket);
                            } else {
                                voiceState.set('flying');
                                if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl?.setLocationLock) {
                                    (window as any).WingmanSpatialControl.setLocationLock(true);
                                }
                                queueEvent({
                                    action: intentType === 'FIT_BOUNDS' ? 'CAMERA_FIT_BOUNDS' : 'CAMERA_MOVE',
                                    params: payload,
                                    source: source as any,
                                    timestamp: Date.now(),
                                });
                            }
                        }
                    } catch (dispatchErr: any) {
                        console.error('[CONTEXT_ENGINE] CAMERA_BRIDGE_ERROR:', dispatchErr);
                    }

                    if (isWebResearch) {
                        ResearchActivityController.completeResearch(requestId);
                    }

                    return {
                        status: 'OK',
                        message: result.message,
                        data: result.data,
                        intent: result.intent,
                        metadata: result.metadata,
                        result: result
                    };
                }
            } catch (error: any) {
                if (isWebResearch) {
                    ResearchActivityController.failResearch(requestId, error?.message || String(error));
                }
                telemetry.error('CONTEXT_ENGINE', 'PIPELINE_ERROR', { error: error.message });
                contextState.setReasoningState('idle');
                // 🛡️ INVARIANT 2: NETWORK FAILURE ≠ CAMERA MOVEMENT
                // Preserve current map/camera state. Never command WORLD_VIEW [lon 0, lat 20] on error.
                const currentData = contextState.getData() || {};
                const fallbackCenter = currentData.center || WORLD_VIEW;
                const fallbackZoom = currentData.zoom || 1.5;
                const fallbackData = {
                    intent: 'fallback',
                    data: {
                        ...currentData,
                        mapMode: currentData.mapMode !== undefined ? currentData.mapMode : true,
                        center: fallbackCenter,
                        zoom: fallbackZoom,
                        markers: currentData.markers || [],
                        panelMode: currentData.panelMode || 'map',
                    }
                };
                contextState.setState(fallbackData.intent, fallbackData.data);
                contextState.setPanelMode(fallbackData.data.panelMode);

                let normalizedMessage = error?.message || String(error);
                if (error?.name === 'AbortError' || error?.name === 'TimeoutError' || normalizedMessage.includes('aborted') || normalizedMessage.includes('timeout')) {
                    normalizedMessage = 'The web research request took longer than expected to gather and summarize live web sources. Please try again or refine your query.';
                }

                return { status: 'ERROR', message: normalizedMessage };
            } finally {
                setTimeout(() => {
                    try {
                        contextState.setReasoningState('idle');
                    } catch (e) { }
                }, 500);
            }
        } catch (fatalError: any) {
            console.error('FATAL ERROR CAUGHT:', fatalError);
            if (typeof require !== 'undefined') require('fs').writeFileSync('fatal_error_log.txt', fatalError.stack || fatalError.message);
            telemetry.error('CONTEXT_ENGINE', 'FATAL_ERROR', { error: fatalError.message });
            return { status: 'ERROR', reason: 'SAFE_FALLBACK' };
        }
    },
    injectVisionObservation(obs: any) {
        contextState.setVisionObservation(obs);
    },
    formatVisionContextForPrompt(maxAgeMs: number = 60000): string {
        const obs = contextState.getVisionObservation(maxAgeMs);
        if (!obs) return "";

        const timeStr = new Date(obs.timestamp || Date.now()).toLocaleTimeString();
        let promptText = `\n[VISUAL CONTEXT]\nSource: ${obs.source || 'CAMERA'}\nObserved: ${timeStr}\nSummary: ${obs.visualSummary || 'Visual scene captured.'}`;

        if (Array.isArray(obs.detectedObjects) && obs.detectedObjects.length > 0) {
            const objectList = obs.detectedObjects.map((o: any) => typeof o === 'string' ? o : o.name).filter(Boolean).join(', ');
            if (objectList) {
                promptText += `\nObjects: ${objectList}`;
            }
        }

        if (obs.ocrText) {
            promptText += `\nOCR Screen Text: "${obs.ocrText}"`;
        }

        return promptText;
    },
    async getSuggestions(query: string) {
        const { mapboxSearchService } = await import('../services/mapboxSearchService');
        return await mapboxSearchService.suggest(query);
    }
};

// Expose contextEngine on window for E2E test harness access (Playwright)
if (typeof window !== 'undefined') {
    (window as any).contextEngine = contextEngine;
    window.addEventListener('wingman:generateReport', (e: any) => {
        const loc = e.detail?.location || useWingmanStore.getState().activeLocationName;
        const prompt = loc ? `Write me a report on ${loc}` : 'Write me a report';
        const classifier = new ReportIntentClassifier();
        const classification = classifier.classify(prompt);
        contextEngine.handleExplicitReportRequest(prompt, classification, 'USER', `report-btn-${Date.now()}`);
    });
}

